import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowRight, ArrowLeft, Sparkles, Check, AlertCircle, Loader2, Info, PlusCircle, Copy, ExternalLink, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { classifyPartV3Stream } from '../services/aiClassificationService';
import ManufacturerAutocomplete from './ManufacturerAutocomplete';
import AIClassificationFlow from './AIClassificationFlow';

const CreatePartModal = ({ isOpen, onClose, onSuccess }) => {
  // Wizard state
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isClassifying, setIsClassifying] = useState(false);
  
  // Streaming abort controller
  const streamCleanupRef = useRef(null);

  // Form data
  const [formData, setFormData] = useState({
    manufacturer: '',
    mpn: '',
    description: '',
    uom: 'EA',
  });

  // Classification data (v3)
  const [classification, setClassification] = useState(null);
  const [selectedClassification, setSelectedClassification] = useState(null);
  const [manualOverride, setManualOverride] = useState(false);
  const [willCreateSubgroup, setWillCreateSubgroup] = useState(false);
  
  // V3 Phase tracking
  const [classificationPhases, setClassificationPhases] = useState([]);
  const [currentPhase, setCurrentPhase] = useState(null);
  const [classificationError, setClassificationError] = useState(null);

  // Taxonomy data for manual selection
  const [mainGroups, setMainGroups] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [selectedMainGroup, setSelectedMainGroup] = useState('');
  const [selectedSubgroupName, setSelectedSubgroupName] = useState('');  // Store NAME, not code
  const [customSubgroupName, setCustomSubgroupName] = useState('');

  // Success modal state - now used for step 4
  const [createdPartData, setCreatedPartData] = useState(null);

  // Load main groups on mount
  useEffect(() => {
    if (isOpen) {
      loadMainGroups();
    }
  }, [isOpen]);

  // Load subgroups when main group changes
  useEffect(() => {
    if (selectedMainGroup) {
      loadSubgroups(selectedMainGroup);
    } else {
      setSubgroups([]);
    }
  }, [selectedMainGroup]);

  // Check if manual selection requires new subgroup
  useEffect(() => {
    if (manualOverride && selectedMainGroup && customSubgroupName) {
      // Check if a subgroup with this name already exists
      const exists = subgroups.some(sg => 
        sg.name.toLowerCase() === customSubgroupName.toLowerCase()
      );
      setWillCreateSubgroup(!exists);
    } else if (manualOverride && selectedMainGroup && selectedSubgroupName) {
      // Selected existing subgroup - no creation needed
      setWillCreateSubgroup(false);
    }
  }, [manualOverride, selectedMainGroup, selectedSubgroupName, customSubgroupName, subgroups]);

  const loadMainGroups = async () => {
    try {
      const response = await api.get('/api/v1/taxonomy/main-groups');
      setMainGroups(response.data || []);
    } catch (error) {
      console.error('Failed to load main groups:', error);
    }
  };

  const loadSubgroups = async (mainGroupCode) => {
    try {
      const response = await api.get(`/api/v1/taxonomy/subgroups/${mainGroupCode}`);
      setSubgroups(response.data || []);
    } catch (error) {
      console.error('Failed to load subgroups:', error);
      setSubgroups([]);
    }
  };

  const resetModal = () => {
    setStep(1);
    setFormData({ manufacturer: '', mpn: '', description: '', uom: 'EA' });
    setClassification(null);
    setSelectedClassification(null);
    setManualOverride(false);
    setWillCreateSubgroup(false);
    setSelectedMainGroup('');
    setSelectedSubgroupName('');
    setCustomSubgroupName('');
    // Reset v3 state
    setClassificationPhases([]);
    setCurrentPhase(null);
    setClassificationError(null);
    // Cleanup stream
    if (streamCleanupRef.current) {
      streamCleanupRef.current();
      streamCleanupRef.current = null;
    }
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamCleanupRef.current) {
        streamCleanupRef.current();
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Step 1: Validate and proceed to classification (using v3 streaming API)
  const handleStep1Next = async () => {
    if (!formData.manufacturer.trim()) {
      toast.error('Please enter a manufacturer name');
      return;
    }
    if (!formData.mpn.trim()) {
      toast.error('Please enter a manufacturer part number (MPN)');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please enter a part description');
      return;
    }

    // Move to step 2 and start AI classification v3 streaming
    setStep(2);
    setIsClassifying(true);
    setClassificationPhases([]);
    setCurrentPhase(null);
    setClassificationError(null);

    // Abort any existing stream
    if (streamCleanupRef.current) {
      streamCleanupRef.current();
    }

    // Start streaming classification
    streamCleanupRef.current = classifyPartV3Stream(
      {
        manufacturer: formData.manufacturer,
        mpn: formData.mpn,
        description: formData.description,
        uom: formData.uom,
      },
      {
        onPhaseStart: (event) => {
          console.log('[AI Stream] Phase started:', event.phase);
          setCurrentPhase(event.phase);
        },
        
        onPhaseComplete: (event) => {
          console.log('[AI Stream] Phase complete:', event.phase, event);
          setClassificationPhases(prev => [...prev, event]);
          setCurrentPhase(null);
        },
        
        onComplete: async (event) => {
          console.log('[AI Stream] Classification complete:', event);
          
          const result = event.result;
          
          // Convert streaming response to expected format
          const v3ToV1Format = {
            primary_suggestion: {
              main_group_code: result.mainGroupCode,
              main_group_name: result.mainGroupName,
              subgroup_name: result.subGroupName,
              subgroup_code: result.subGroupId,
              confidence: result.confidence === 'high' ? 0.9 : result.confidence === 'medium' ? 0.7 : 0.5,
              rationale: event.phases?.find(p => p.phase === 'phase2')?.output?.matchReasoning || 
                        event.phases?.find(p => p.phase === 'phase4')?.output?.description || '',
              subgroup_exists: !result.isNewSubGroup
            },
            confidence: result.confidence === 'high' ? 0.9 : result.confidence === 'medium' ? 0.7 : 0.5,
            confidence_level: result.confidence?.toUpperCase() || 'MEDIUM',
            alternative_suggestions: [],
            phase4_metadata: event.phase4Metadata,
            totalDurationMs: event.totalDurationMs,
            totalApiCalls: event.totalApiCalls
          };

          setClassification(v3ToV1Format);
          setSelectedClassification(v3ToV1Format.primary_suggestion);
          setSelectedMainGroup(result.mainGroupCode);
          
          // Check if subgroup exists
          setWillCreateSubgroup(result.isNewSubGroup);
          setCustomSubgroupName(result.subGroupName);
          
          // Load subgroups for the suggested main group
          try {
            await loadSubgroups(result.mainGroupCode);
            if (!result.isNewSubGroup) {
              setSelectedSubgroupName(result.subGroupName);
            }
          } catch (e) {
            console.error('Failed to load subgroups:', e);
          }
          
          setIsClassifying(false);
          setCurrentPhase(null);
        },
        
        onError: (error) => {
          console.error('[AI Stream] Error:', error);
          setClassificationError(error.message || 'AI classification failed');
          toast.error('AI classification failed. Please select manually.');
          setManualOverride(true);
          setIsClassifying(false);
          setCurrentPhase(null);
        }
      }
    );
  };

  // Step 2: Proceed to review
  const handleStep2Next = () => {
    if (manualOverride) {
      if (!selectedMainGroup) {
        toast.error('Please select a main group');
        return;
      }
      // Check if either existing subgroup selected OR custom name provided
      if (!selectedSubgroupName && !customSubgroupName.trim()) {
        toast.error('Please select a subgroup or enter a name for a new subgroup');
        return;
      }
    }
    setStep(3);
  };

  // Step 3: Create the part
  const handleCreatePart = async () => {
    setIsSubmitting(true);

    try {
      const mainGroupCode = manualOverride ? selectedMainGroup : selectedClassification?.main_group_code;
      // Use subgroup NAME (not code) - system allocates code via PRP
      const subgroupName = willCreateSubgroup 
        ? customSubgroupName 
        : (manualOverride ? selectedSubgroupName : selectedClassification?.subgroup_name);

      // Build ai_classification_data from classification phases
      let aiClassificationData = null;
      if (classificationPhases?.length > 0) {
        // Calculate total duration from phases
        const totalDurationMs = classificationPhases.reduce((sum, p) => sum + (p.durationMs || 0), 0);
        
        aiClassificationData = {
          classified_at: new Date().toISOString(),
          phases: {},
          totalDurationMs,
        };
        
        classificationPhases.forEach(phase => {
          if (phase.phase && phase.output) {
            aiClassificationData.phases[phase.phase] = {
              output: phase.output,
              durationMs: phase.durationMs || 0,
            };
          }
        });
        
        // Add final result
        if (selectedClassification) {
          aiClassificationData.finalResult = {
            mainGroupCode: selectedClassification.main_group_code,
            mainGroupName: selectedClassification.main_group_name,
            subGroupCode: selectedClassification.subgroup_code,
            subGroupName: selectedClassification.subgroup_name,
            isNewSubGroup: willCreateSubgroup,
          };
        }
      }

      const response = await api.post('/api/v1/parts/create', {
        manufacturer: formData.manufacturer,
        mpn: formData.mpn,
        description: formData.description,
        uom: formData.uom,
        main_group_code: mainGroupCode,
        subgroup_name: subgroupName,  // NAME only, system allocates code
        ai_classification_data: aiClassificationData,
      });

      // Get main group name for display
      const mainGroupName = getMainGroupName(mainGroupCode);

      // Prepare data for success step (step 4)
      const partDataForModal = {
        ...response.data,
        manufacturer: formData.manufacturer,
        mpn: formData.mpn,
        description: formData.description,
        main_group_name: mainGroupName,
        subgroup_name: subgroupName,
      };

      // Go to step 4 (confirmation) instead of showing separate modal
      setCreatedPartData(partDataForModal);
      setStep(4);
      
      // Notify parent of success (for refreshing lists)
      onSuccess?.(response.data);

    } catch (error) {
      console.error('Failed to create part:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create part';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle "Create Another" from step 4
  const handleCreateAnother = () => {
    setCreatedPartData(null);
    // Reset form to step 1
    setStep(1);
    setFormData({ manufacturer: '', mpn: '', description: '', uom: 'EA' });
    setClassification(null);
    setSelectedClassification(null);
    setManualOverride(false);
    setWillCreateSubgroup(false);
    setSelectedMainGroup('');
    setSelectedSubgroupName('');
    setCustomSubgroupName('');
  };

  // Handle "View Part" from step 4
  const handleViewPart = () => {
    handleClose();
    // Open part in the overview drawer on the parts list
    if (createdPartData?.hpn_compact) {
      window.location.href = `/parts?panel=${createdPartData.hpn_compact}`;
    }
  };

  // Handle close from step 4
  const handleCloseFromSuccess = () => {
    setCreatedPartData(null);
    handleClose();
  };

  // Copy HPN to clipboard
  const copyHPN = () => {
    if (createdPartData?.hpn_display) {
      navigator.clipboard.writeText(createdPartData.hpn_display);
      toast.success('HPN copied to clipboard');
    }
  };

  // Select alternative classification
  const handleSelectAlternative = (suggestion) => {
    setSelectedClassification(suggestion);
    setSelectedMainGroup(suggestion.main_group_code);
    setWillCreateSubgroup(!suggestion.subgroup_exists);
    setCustomSubgroupName(suggestion.subgroup_name);
    loadSubgroups(suggestion.main_group_code).then(() => {
      if (suggestion.subgroup_exists) {
        setSelectedSubgroupName(suggestion.subgroup_name);
      }
    });
    setManualOverride(false);
  };

  // Toggle manual override
  const handleManualOverride = () => {
    setManualOverride(true);
    setSelectedClassification(null);
    setWillCreateSubgroup(false);
    setCustomSubgroupName('');
  };

  if (!isOpen) return null;

  const getConfidenceColor = (level) => {
    switch (level) {
      case 'HIGH': return 'text-green-600 bg-green-100';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-100';
      case 'LOW': return 'text-red-600 bg-red-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  const getMainGroupName = (code) => {
    const group = mainGroups.find(g => g.code === code);
    return group ? group.name : code;
  };

  const getSubgroupNameFromCode = (mainCode, subCode) => {
    const subgroup = subgroups.find(s => s.main_group_code === mainCode && s.subgroup_code === subCode);
    return subgroup ? subgroup.name : customSubgroupName || subCode;
  };

  const finalMainGroupCode = manualOverride ? selectedMainGroup : selectedClassification?.main_group_code;
  const finalSubgroupName = manualOverride 
    ? (willCreateSubgroup ? customSubgroupName : selectedSubgroupName)
    : selectedClassification?.subgroup_name;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal - Wider for Step 2 AI Classification */}
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full mx-4 max-h-[90vh] overflow-hidden animate-fade-in ${
        step === 2 ? 'max-w-5xl' : 'max-w-2xl'
      }`}>
        {/* Header */}
        <div className="bg-haden-dark text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Create New Part</h2>
            <p className="text-sm text-slate-300">
              Step {step} of 4 • {step === 1 ? 'Part Info' : step === 2 ? 'AI Classification' : step === 3 ? 'Review' : 'Confirmation'}
            </p>
          </div>
          <button
            onClick={step === 4 ? handleCloseFromSuccess : handleClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="close-modal-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Bar with Step Indicators */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-between max-w-md mx-auto">
            {[
              { num: 1, label: 'Info' },
              { num: 2, label: 'AI' },
              { num: 3, label: 'Review' },
              { num: 4, label: 'Confirm' }
            ].map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                    step > s.num 
                      ? 'bg-green-500 text-white' 
                      : step === s.num 
                        ? 'bg-haden-accent text-white' 
                        : 'bg-slate-200 text-slate-500'
                  }`}>
                    {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                  </div>
                  <span className={`text-xs mt-1 ${
                    step >= s.num ? 'text-slate-700 font-medium' : 'text-slate-400'
                  }`}>{s.label}</span>
                </div>
                {idx < 3 && (
                  <div className={`flex-1 h-0.5 mx-2 transition-colors ${
                    step > s.num ? 'bg-green-500' : 'bg-slate-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-haden-accent rounded-full flex items-center justify-center text-white font-semibold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Part Information</h3>
                  <p className="text-sm text-slate-500">Enter the manufacturer and part details</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Manufacturer <span className="text-red-500">*</span>
                  </label>
                  <ManufacturerAutocomplete
                    value={formData.manufacturer}
                    onChange={(value) => setFormData(prev => ({ ...prev, manufacturer: value }))}
                    placeholder="Start typing to search existing manufacturers..."
                    data-testid="input-manufacturer"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Select an existing manufacturer or create a new one
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Manufacturer Part Number (MPN) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="mpn"
                    value={formData.mpn}
                    onChange={handleInputChange}
                    placeholder="e.g., SH105A4AAA, 6ES7214-1AG40-0XB0"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent font-mono"
                    data-testid="input-mpn"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter a detailed description of the part..."
                    rows={3}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent resize-none"
                    data-testid="input-description"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Include specifications like voltage, capacity, model series for better AI classification
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Unit of Measure
                  </label>
                  <select
                    name="uom"
                    value={formData.uom}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent"
                    data-testid="input-uom"
                  >
                    <option value="EA">EA - Each</option>
                    <option value="M">M - Meter</option>
                    <option value="FT">FT - Feet</option>
                    <option value="KG">KG - Kilogram</option>
                    <option value="L">L - Liter</option>
                    <option value="SET">SET - Set</option>
                    <option value="BOX">BOX - Box</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: AI Classification */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-2">
              {/* Use the new AIClassificationFlow for immersive experience */}
              {(isClassifying || (classificationPhases.length > 0 && !manualOverride)) ? (
                <AIClassificationFlow
                  partData={{
                    manufacturer: formData.manufacturer,
                    mpn: formData.mpn,
                    description: formData.description,
                    uom: formData.uom
                  }}
                  phases={classificationPhases}
                  currentPhase={currentPhase}
                  isComplete={!isClassifying && classification !== null}
                  isClassifying={isClassifying}
                  totalDurationMs={classificationPhases.reduce((sum, p) => sum + (p.durationMs || 0), 0)}
                  totalApiCalls={classificationPhases.filter(p => p.phase !== 'phase2_5').length}
                  classification={classification}
                  error={classificationError}
                  onAccept={classification ? handleStep2Next : null}
                  onAdjust={() => setManualOverride(true)}
                />
              ) : (
                /* Manual Override Mode */
                <div className="space-y-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-haden-accent rounded-full flex items-center justify-center text-white font-semibold">
                      2
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Manual Classification</h3>
                      <p className="text-sm text-slate-500">Select classification manually</p>
                    </div>
                  </div>

                  {/* Manual Override */}
                  <div className="space-y-4 border-2 border-amber-200 bg-amber-50 rounded-xl p-4">
                    <div className="flex items-center space-x-2 text-amber-700">
                      <AlertCircle className="w-5 h-5" />
                      <p className="font-medium">Manual Classification</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Main Group <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={selectedMainGroup}
                        onChange={(e) => {
                          setSelectedMainGroup(e.target.value);
                          setSelectedSubgroupName('');
                          setWillCreateSubgroup(false);
                          setCustomSubgroupName('');
                        }}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent"
                        data-testid="select-main-group"
                      >
                        <option value="">Select a main group...</option>
                        {mainGroups.map((group) => (
                          <option key={group.code} value={group.code}>
                            {group.code} - {group.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Subgroup <span className="text-red-500">*</span>
                      </label>
                      {subgroups.length > 0 ? (
                        <select
                          value={selectedSubgroupName}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '__new__') {
                              setSelectedSubgroupName('');
                              setWillCreateSubgroup(true);
                            } else {
                              setSelectedSubgroupName(value);
                              setWillCreateSubgroup(false);
                              setCustomSubgroupName('');
                            }
                          }}
                          disabled={!selectedMainGroup}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent disabled:bg-slate-100 disabled:cursor-not-allowed"
                          data-testid="select-subgroup"
                        >
                          <option value="">Select a subgroup...</option>
                          {subgroups.map((sg) => (
                            <option key={sg.subgroup_code} value={sg.name}>
                              {sg.name} ({sg.subgroup_code})
                            </option>
                          ))}
                          <option value="__new__">+ Create new subgroup</option>
                        </select>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-sm text-amber-600 mb-2">
                            No subgroups exist for this main group. Enter a name for the new subgroup.
                          </p>
                          <input
                            type="text"
                            value={customSubgroupName}
                            onChange={(e) => {
                              setCustomSubgroupName(e.target.value);
                              setWillCreateSubgroup(true);
                            }}
                            placeholder="Enter subgroup name (e.g., Scroll Compressors)"
                            disabled={!selectedMainGroup}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent disabled:bg-slate-100 disabled:cursor-not-allowed"
                            data-testid="input-subgroup-name"
                          />
                        </div>
                      )}
                    </div>

                    {/* New Subgroup Name Input - when creating from dropdown */}
                    {willCreateSubgroup && subgroups.length > 0 && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-3">
                        <div className="flex items-center space-x-2 text-green-700">
                          <PlusCircle className="w-4 h-4" />
                          <p className="text-sm font-medium">New subgroup will be created (code auto-assigned)</p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Subgroup Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={customSubgroupName}
                            onChange={(e) => setCustomSubgroupName(e.target.value)}
                            placeholder="e.g., Industrial Relays, Servo Motors"
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            data-testid="input-new-subgroup-name"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            The subgroup code will be automatically generated by the system.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Review & Confirm */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-3">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-haden-accent rounded-full flex items-center justify-center text-white font-semibold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Review & Confirm</h3>
                  <p className="text-sm text-slate-500">Verify the details before creating</p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Manufacturer</p>
                    <p className="font-medium text-slate-900">{formData.manufacturer}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">MPN</p>
                    <p className="font-medium font-mono text-slate-900">{formData.mpn}</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Description</p>
                  <p className="font-medium text-slate-900">{formData.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Unit of Measure</p>
                    <p className="font-medium text-slate-900">{formData.uom}</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Classification</p>
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <div className="flex items-center space-x-2">
                      <span className="font-mono font-semibold text-haden-primary">
                        {finalMainGroupCode}
                      </span>
                      <span className="text-slate-400">→</span>
                      <span className="font-semibold text-haden-secondary">
                        {finalSubgroupName}
                      </span>
                      {willCreateSubgroup && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <PlusCircle className="w-3 h-3 mr-1" />
                          New
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {getMainGroupName(finalMainGroupCode)}
                    </p>
                    {willCreateSubgroup && (
                      <p className="text-xs text-slate-400 mt-1">
                        Subgroup code will be auto-assigned by the system
                      </p>
                    )}
                    {!manualOverride && classification && (
                      <p className="text-xs text-slate-400 mt-2">
                        AI confidence: {Math.round(classification.confidence * 100)}%
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* New Subgroup Creation Notice */}
              {willCreateSubgroup && (
                <div className="flex items-start space-x-2 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <PlusCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-700">
                    <p className="font-medium">A new subgroup will be created</p>
                    <p className="text-green-600 mt-1">
                      The subgroup &ldquo;<span className="font-semibold">{finalSubgroupName}</span>&rdquo; 
                      will be added to main group {finalMainGroupCode} with an auto-assigned code.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-start space-x-2 p-4 bg-blue-50 rounded-lg">
                <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium">A unique HPN will be generated automatically</p>
                  <p className="text-blue-600 mt-1">
                    The Haden Part Number will be assigned based on the selected classification.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Confirmation/Success */}
          {step === 4 && createdPartData && (
            <div className="space-y-6" data-testid="step-4">
              {/* Success Header */}
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900">Part Created Successfully!</h3>
                <p className="text-sm text-slate-500 mt-1">Your new part has been added to the catalogue</p>
              </div>

              {/* HPN Display Card */}
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 text-center">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Haden Part Number</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-3xl font-mono font-bold text-white tracking-wider">
                    {createdPartData.hpn_display}
                  </span>
                  <button
                    onClick={copyHPN}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Copy HPN"
                  >
                    <Copy className="w-5 h-5 text-slate-400 hover:text-white" />
                  </button>
                </div>
              </div>

              {/* Part Details Summary */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-sm text-slate-500">Description</span>
                  <span className="text-sm text-slate-900 font-medium text-right max-w-[60%]">
                    {createdPartData.description}
                  </span>
                </div>
                <div className="border-t border-slate-200" />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Manufacturer</span>
                  <span className="text-sm text-slate-900 font-medium">{createdPartData.manufacturer}</span>
                </div>
                <div className="border-t border-slate-200" />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">MPN</span>
                  <span className="text-sm font-mono text-slate-900">{createdPartData.mpn}</span>
                </div>
                <div className="border-t border-slate-200" />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Classification</span>
                  <div className="text-right">
                    <span className="text-sm text-slate-900 font-medium">
                      {createdPartData.main_group_code} - {createdPartData.main_group_name}
                    </span>
                    <p className="text-xs text-slate-500">
                      {createdPartData.subgroup_code} - {createdPartData.subgroup_name}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  onClick={handleCreateAnother}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors font-medium"
                  data-testid="create-another-button"
                >
                  <RotateCcw className="w-4 h-4" />
                  Create Another
                </button>
                <button
                  onClick={handleViewPart}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-haden-accent hover:bg-haden-secondary text-white rounded-lg transition-colors font-medium"
                  data-testid="view-part-button"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Part Details
                </button>
              </div>
              
              <div className="text-center">
                <button
                  onClick={handleCloseFromSuccess}
                  className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
                  data-testid="close-success-button"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Hide on Step 4 (success has its own actions) */}
        {step < 4 && (
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
            <div>
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  disabled={isSubmitting || isClassifying}
                  className="flex items-center space-x-2 px-4 py-2 text-slate-600 hover:text-slate-900 disabled:opacity-50"
                  data-testid="back-button"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back</span>
                </button>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-slate-600 hover:text-slate-900"
                data-testid="cancel-button"
              >
                Cancel
              </button>

              {step < 3 && (
                <button
                  onClick={step === 1 ? handleStep1Next : handleStep2Next}
                  disabled={isClassifying}
                  className="flex items-center space-x-2 px-6 py-2 bg-haden-accent text-white rounded-lg hover:bg-haden-secondary transition-colors disabled:opacity-50"
                  data-testid="next-button"
                >
                  <span>{step === 1 ? 'Classify with AI' : 'Review'}</span>
                  {step === 1 ? <Sparkles className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                </button>
              )}

              {step === 3 && (
                <button
                  onClick={handleCreatePart}
                  disabled={isSubmitting}
                  className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  data-testid="create-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Create Part</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatePartModal;
